import { useState } from "react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Gift, Search, CheckCircle2, XCircle, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const fmtR = (v) =>
  "R " + (Number(v) || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");

/**
 * Voucher redemption dialog. Cashier types/scans the code, we validate it,
 * and they pick "Apply as discount" or "Redeem for airtime". On success we
 * push the redeem_line straight into the cart via onAddLine.
 */
export default function RedeemVoucherDialog({ open, onClose, onAddLine, currentSaleNumber, currentCashier }) {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [voucher, setVoucher] = useState(null);
  const [redeeming, setRedeeming] = useState(false);

  const reset = () => {
    setCode("");
    setVoucher(null);
    setLoading(false);
    setRedeeming(false);
  };

  const handleClose = () => {
    reset();
    onClose && onClose();
  };

  const lookup = async () => {
    const c = code.trim().toUpperCase();
    if (!c) {
      toast.error("Enter a voucher code");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.get(`${API}/vouchers/lookup/${encodeURIComponent(c)}`);
      setVoucher(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Voucher not found");
      setVoucher(null);
    } finally {
      setLoading(false);
    }
  };

  const redeem = async (redeem_as) => {
    if (!voucher) return;
    setRedeeming(true);
    try {
      const res = await axios.post(`${API}/vouchers/redeem`, {
        code: voucher.code,
        redeem_as,
        redeemed_sale_number: currentSaleNumber || null,
        redeemed_cashier: currentCashier || null,
      });
      // Add the pre-built redeem_line to the cart. The parent Sales
      // page decides whether to also open the Flash airtime dialog
      // (redeem_as === "airtime") — flagged on the line.
      if (onAddLine) {
        onAddLine(res.data.redeem_line, res.data.voucher);
      }
      toast.success(
        redeem_as === "discount"
          ? `Voucher ${voucher.code} redeemed as ${fmtR(voucher.voucher_value)} discount`
          : `Voucher ${voucher.code} redeemed for ${fmtR(voucher.voucher_value)} airtime — complete the top-up now`
      );
      handleClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to redeem voucher");
    } finally {
      setRedeeming(false);
    }
  };

  const isActive = voucher?.status === "ACTIVE";
  const statusBadge = () => {
    if (!voucher) return null;
    const s = voucher.status;
    if (s === "ACTIVE") return <Badge className="bg-emerald-600">ACTIVE</Badge>;
    if (s === "REDEEMED") return <Badge className="bg-gray-500">REDEEMED</Badge>;
    if (s === "EXPIRED") return <Badge className="bg-amber-600">EXPIRED</Badge>;
    return <Badge className="bg-red-600">CANCELLED</Badge>;
  };

  return (
    <Dialog open={open} onOpenChange={(o) => (!o ? handleClose() : null)}>
      <DialogContent className="max-w-md" data-testid="redeem-voucher-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Gift className="h-5 w-5 text-purple-600" /> Redeem Voucher
          </DialogTitle>
          <DialogDescription>
            Enter the voucher code from the customer&apos;s slip.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label className="text-xs">Voucher Code</Label>
            <div className="flex gap-2">
              <Input
                autoFocus
                placeholder="VCH-XXXXXX"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                onKeyDown={(e) => e.key === "Enter" && lookup()}
                className="font-mono uppercase"
                data-testid="voucher-code-input"
              />
              <Button
                onClick={lookup}
                disabled={loading || !code.trim()}
                data-testid="voucher-lookup-btn"
              >
                {loading ? (
                  <RefreshCw className="h-4 w-4 animate-spin" />
                ) : (
                  <Search className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>

          {voucher && (
            <div
              className={`border rounded-lg p-3 space-y-1 text-sm ${
                isActive ? "bg-emerald-50 border-emerald-200" : "bg-gray-50 border-gray-200"
              }`}
              data-testid="voucher-lookup-result"
            >
              <div className="flex items-center justify-between">
                <span className="font-mono font-bold">{voucher.code}</span>
                {statusBadge()}
              </div>
              <div className="text-gray-700">
                {voucher.voucher_description || voucher.promotion_name}
              </div>
              <div className="text-xl font-bold text-purple-700">
                {fmtR(voucher.voucher_value)}
              </div>
              <div className="text-xs text-gray-600">
                Expires{" "}
                {voucher.expiry_date
                  ? new Date(voucher.expiry_date).toLocaleDateString("en-ZA", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    })
                  : "—"}
              </div>
              {voucher.issued_sale_number && (
                <div className="text-xs text-gray-500">
                  Issued on {voucher.issued_sale_number}
                </div>
              )}
              {voucher.status === "REDEEMED" && (
                <div className="text-xs text-gray-500">
                  Redeemed{" "}
                  {voucher.redeemed_at
                    ? new Date(voucher.redeemed_at).toLocaleString("en-ZA")
                    : ""}{" "}
                  {voucher.redeemed_as ? `(${voucher.redeemed_as})` : ""}
                </div>
              )}
            </div>
          )}

          {voucher && !isActive && (
            <div className="flex items-center gap-2 text-red-700 text-sm">
              <XCircle className="h-4 w-4" /> This voucher cannot be redeemed.
            </div>
          )}

          {isActive && (
            <>
              <div className="text-xs text-gray-600 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                <strong>Note:</strong> Single-use only. No change is given if
                the customer spends less than the voucher value.
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  onClick={() => redeem("discount")}
                  disabled={redeeming}
                  className="bg-purple-600 hover:bg-purple-700"
                  data-testid="voucher-redeem-discount"
                >
                  {redeeming ? (
                    <RefreshCw className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <CheckCircle2 className="h-4 w-4 mr-1" /> Apply as discount
                    </>
                  )}
                </Button>
                <Button
                  onClick={() => redeem("airtime")}
                  disabled={redeeming}
                  variant="outline"
                  className="border-purple-300 text-purple-700"
                  data-testid="voucher-redeem-airtime"
                >
                  {redeeming ? (
                    <RefreshCw className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <CheckCircle2 className="h-4 w-4 mr-1" /> Redeem for airtime
                    </>
                  )}
                </Button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
